// @ts-strict-ignore
// TODO: Fix with MBCC-2545
// this file has multiple (independent) issues with any; needs further investigation
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Location } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { inject as ngInject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { createSelector, Store, type Action } from '@ngrx/store';
import { EMPTY, noop, of, type Observable } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  skipWhile,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import type {
  ComponentCategory,
  MotorizationVehicle,
  SelectablesResponseVehicleComponent,
  Vehicle,
  VehicleComponent
} from 'src/app/api/owcc/models';
import { ChangesService, SelectablesService, VehiclesService } from 'src/app/api/owcc/services';
import { appInject } from 'src/app/app-injector';
import { AppErrorMajor } from 'src/app/core/app-error/app-error-major.enum';
import { AppErrorMinor } from 'src/app/core/app-error/app-error-minor.enum';
import type { AppError, AppErrorWithNotification } from 'src/app/core/app-error/app-error.interface';
import { appErrorMajor, appErrorMinor } from 'src/app/core/app-error/store/app-error.actions';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { LinkService } from 'src/app/core/services/link/link.service';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { selectApiContext } from 'src/app/select-api-context';
import {
  AdditionalChangesInitiator,
  type FeasibilityResult
} from 'src/app/shared/feasibility-conflict-modal/feasibility-result.interface';
import { ModalService } from 'src/app/shared/modal/modal-service/modal.service';
import type { NavigationGroup } from 'src/app/shared/navigation/navigation-group.interface';
import { translate } from 'src/app/shared/pipes/translate/translate.pipe';
import {
  invalidateOnlineCode,
  startSetNavigationGroups,
  updateBaumuster,
  updateCurrentMotorization,
  updateStage,
  updateVehicleId,
  updateVehicleIdForUpcomingConfiguration,
  type CommonActionType
} from 'src/app/store/common/common.actions';
import {
  fromNavigation,
  selectConfigurationComponentInfo,
  selectConfigurationVehicle,
  selectConfigurationVehicleId,
  selectSelectableChangeAndQueue,
  selectVehicleIdAndCurrentVehicleId,
  selectVehicleInludingComponentInfo
} from 'src/app/store/common/common.selectors';
import { getModelIdFromVehicleId } from 'src/app/store/common/common.utils';
import {
  routerContextUpdatedAction,
  routerGuardsCheckEndAction,
  routerNavigationEndAction
} from 'src/app/store/router/router.actions';
import { selectFilteredPreviousRouteState, selectRouteState } from 'src/app/store/router/router.selectors';
import { runtimeVariablesUpdate } from 'src/app/store/runtime-variables/runtime-variables.actions';
import { selectContextVehicleId } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import type { EnrichedComponentCategory } from 'src/app/utils/models/enriched-component-category.type';
import type { ExtendedVehicleComponentInfoCollection } from 'src/app/utils/models/extended-vehicle-component-info-collection.interface';
import type { VehicleComponentCollection } from 'src/app/utils/models/vehicle-component-collection.interface';
import { VehicleSelectableChange } from 'src/app/utils/models/vehicle-selectable-change.enum';

import {
  selectMotorizationVehicles,
  selectMotorizationVehiclesByVehicleLengthAndMotorizationType
} from '../../motorization/store/motorization.selectors';

import {
  abortFeasibilityConflictAction,
  acceptChangesAction,
  finishEnqueueSelectableChangeAction,
  finishInitializeAction,
  finishLoadComponentInfoForCurrentVehicleAction,
  finishLoadFeasibilityResultAction,
  finishLoadSelectablesAction,
  finishLoadVehicleAction,
  finishProcessFeasibilityConflictResultAction,
  finishProcessFeasibilityResultAction,
  finishProcessSelectableChangeAction,
  finishRequestSelectableChangeFromQueueAction,
  finishResolveFeasibilityConflictAction,
  finishUpdateFeasibleVehicleAction,
  finishUpdateLocationStateAction,
  finishUpdateVehicleAction,
  finishUpdateVehicleContextAction,
  resetVehicleAction,
  startEnqueueSelectableChangeAction,
  startInitializeAction,
  startLoadComponentInfoForCurrentVehicleAction,
  startLoadFeasibilityResultAction,
  startLoadSelectablesAction,
  startLoadVehicle,
  startProcessFeasibilityConflictResultAction,
  startProcessFeasibilityResultAction,
  startProcessSelectableChangeAction,
  startRequestSelectableChangeFromQueueAction,
  startResolveFeasibilityConflictAction,
  startUpdateFeasibleVehicleAction,
  startUpdateLocationStateAction,
  startUpdateVehicleAction,
  startUpdateVehicleContextAction,
  updateIsInitialMotorizationAction,
  type ConfigurationActionType
} from './configuration.actions';

// TODO: this graph is and was outdated!
/*
  A VISUALIZATION OF CONFIGURATION STORE'S ACTION PIPELINES
  =========================================================
  +---------------------------+
  | Enqueue Selectable Change |
  +---------+-----------------+
            |
            v
  +---------+----------------------------+
  | Request Selectable Change from Queue |
  +-----+---------------------------+----+
        ^                           |
        |                           | if no change in process
        |                           v
        |                        +--+------------------------------+
        |                        | START Process Selectable Change |
        |                        +--+------------------------------+
        |                           |
        |                           v
        |                        +--+----------------------+
        |                        | Load Feasibility Result |
        |                        +--+----------------------+
        |                           |
        |                           v
        |                        +--+-------------------------+
        |                        | Process Feasibility Result |
        |                        +-------------+--------------+    +-------------------------------+
        |                              SUCCESS | CONFLICT +------> | Resolve Feasibility Conflict  |
        |                               +                          | (Modal with User Interaction) |
        |                               |                          +--------------+----------------+
        |                               v                                         |
        |                        +------+------------------+                      v
        |                        | Change Feasible Vehicle |       +--------------+----------------------+
        |                        +-+--------+-----------+--+       | Process Feasibility Conflict Result |
        |                          |        |           |          +----+---------------+----------------+
        |                          |        |           |               |               |                               +------------------------+
        |                          v        |           |               |               |                               | Router Context Updated |
  +-----+--------------------------+-+      |           |               |               | if alternative aappepted       +--+---------------------+
  | FINISH Process Selectable Change | <--------------------------------+               |                                  |
  +----------------------------------+      |           |                               |                                  |
                                            |           |                               |                                  | if configuration page entered
                                            |           |                               |                                  |
                                            |           |                               |                                  |
                                            |           v                               v                                  v
              +----------------+            |    +------+-------------------+          +--+-----------------+             +--+-----------+
              | Update Context | <----------+    | START Load ComponentInfo | <--------+ START Load Vehicle | <-----------+ Init / Reset |
              +----------------+            |    +------+-------------------+          +--+-----------------+             +--------------+
                                            |           |                               |
                                            |           |                               |
                                            |           v                               |
                                            |     +------+-----------------+            |
                                            |     | START Load Selectables |            |
                                            |     +------+-----------------+            |
                                            |           |                               |
                                            |           |                               |
                                            |           v                               v
      +------------------------+            |    +------+------------------+         +--+-------------------+
      | Update Location State  | <----------+    | FINISH Load Selectables |         | FINISH Load Vehicles |
      +------------------------+            |    +-------------------------+         +--+-------------------+
                                            |                                           |
                                            +-------------------------------------------+
*/

interface PayloadVehicleConfig {
  vehicle?: MotorizationVehicle;
  modelId?: string;
  vehicleId?: string;
}

/**
 * If a configuration route is about to activate and currently active route
 * is no configuration route, initialize configuration store
 * [router context updated]
 *   -> (conditional) [start initialize]
 */
export const routeChange$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(routerContextUpdatedAction),
      filter(({ routeState }) => routeState?.isConfiguration),
      mergeMap(() => store$.select(selectRouteState).pipe(first())), // this is not necessary as we have the route state already in the action
      filter(routeState => !routeState?.isConfiguration),

      /**
       * at this point we know that we're new to configuration page,
       * therefore we check if the store needs to be flushed,
       * if lastUsedBaumuster differs from new vehicle's baumuster
       */
      mergeMap(() => store$.select(selectVehicleIdAndCurrentVehicleId).pipe(first())),
      filter(({ vehicleId, currentVehicleId }) => vehicleId !== currentVehicleId),
      map(() => startInitializeAction())
    ),
  { functional: true, dispatch: true }
);

/**
 * If a configuration route is about to activate and it's not a configuration
 * step, redirect to first step of current vehicle.
 * [router guards check end]
 *   -> (conditional) redirect to first configuration step
 */
export const toFirstStep$ = createEffect(
  (actions$ = ngInject(Actions), store$ = ngInject(Store)): Observable<void> =>
    actions$.pipe(
      ofType(routerGuardsCheckEndAction),
      filter(({ routeState }) => routeState && routeState.isConfiguration && !routeState.isConfigurationStep),
      mergeMap(({ routeState }) =>
        store$
          .select(fromNavigation.selectNavigationGroups)
          .pipe(filterTruthy(), first(), withLatestFrom(of(routeState.pathParams.configurationStep)))
      ),
      mergeMap(([items, latestConfigStep]: [NavigationGroup[], string]): Observable<[NavigationGroup[], number]> => {
        // Check if it is a link to a main group like "exterior", "interior" etc., if so we want to go to the first navigation item in that group
        // As items[0] is always "motorization", fall back to items[1] if we didn't find a matching group
        latestConfigStep = String(latestConfigStep).toUpperCase();
        let groupIndex: number = items.findIndex(
          (navigationGroup: NavigationGroup): boolean =>
            !!navigationGroup.id && navigationGroup.id.toUpperCase() === latestConfigStep
        );
        groupIndex =
          groupIndex > 1 && !!items[groupIndex].children && items[groupIndex].children.length > 0 ? groupIndex : 1;

        return of([items, groupIndex]);
      }),
      skipWhile(
        ([items, groupIndex]: [NavigationGroup[], number]): boolean =>
          items.length === 0 || items[groupIndex].children.length === 0
      ),
      mergeMap(
        ([items, groupIndex]: [NavigationGroup[], number]): Observable<string | string[]> =>
          // take the first child (NavigationItem), of that group and create the linkArray
          appInject<LinkService>(LinkService)
            .getLinkArray(AppRoutePath.Configuration, items[groupIndex].children[0].pathParams)
            .pipe(first())
      ),
      map((linkArray: string | string[]): void => {
        appInject<Router>(Router)
          .navigate(Array.isArray(linkArray) ? linkArray : [linkArray], {
            replaceUrl: true,
            queryParamsHandling: 'preserve'
          })
          .catch(noop);
      })
    ),
  { functional: true, dispatch: false }
);

/**
 * When navigated to a configuration step route, and previous page was also a
 * configuration step route, ensure location state has current vehicle id.
 * [router navigation end]
 *   -> (conditional) [start update location state]
 */
export const ensureLocationState$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(routerNavigationEndAction),
      filter(({ routeState }) => routeState?.isConfigurationStep),
      mergeMap(() => store$.pipe(selectFilteredPreviousRouteState, first())),
      filter(previousRouteState => previousRouteState?.isConfigurationStep),
      mergeMap(() => store$.select(selectConfigurationVehicle).pipe(first())),
      map(({ vehicleId }) => startUpdateLocationStateAction({ vehicleId }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Initialize/reset configuration state
 * [start initialize]
 *   -> [finish initialize]
 */
export const initialize$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startInitializeAction),
      map(() => finishInitializeAction())
    ),
  { functional: true, dispatch: true }
);

/**
 * After initialization, load Vehicle
 * [finish initialize]
 *   -> [start load vehicle]
 */
export const triggerLoadVehicleAfterInitialize$ = createEffect(
  (
    store$ = ngInject(Store),
    actions$ = ngInject(Actions)
  ): Observable<Action<ConfigurationActionType.StartLoadVehicle>> =>
    actions$.pipe(
      ofType(finishInitializeAction),
      mergeMap(() => store$.pipe(selectContextVehicleId, first())),
      map(vehicleId => startLoadVehicle({ vehicleId }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Load additional component info
 * [start load componentInfo]
 *   -> [finish load componentInfo]
 */
export const loadComponentInfo$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startLoadComponentInfoForCurrentVehicleAction, updateCurrentMotorization),
      concatLatestFrom(() => store$.select(selectApiContext)),
      distinctUntilChanged(([prevPayload], [currPayload]) => {
        // because we listen to different actions, we compare apples with pears, so we first pull the id's from the different payloads
        const getVehicleId = (data: PayloadVehicleConfig): string =>
          'vehicle' in data && data.vehicle !== undefined ? data.vehicle.vehicleId : data.vehicleId;

        const [dataPrev, dataCurr] = [prevPayload, currPayload].map(
          payload => ({ ...payload }) as PayloadVehicleConfig
        );

        return getVehicleId(dataPrev) === getVehicleId(dataCurr);
      }),
      mergeMap(([payload, { market, subProcess, dataVersion }]) => {
        const data = { ...payload } as PayloadVehicleConfig;

        return appInject<SelectablesService>(SelectablesService)
          .getComponentsInfo({ market, subProcess, dataVersion, modelId: data?.modelId ?? data?.vehicle?.modelId })
          .pipe(
            map(componentInfo =>
              finishLoadComponentInfoForCurrentVehicleAction({
                componentInfo: componentInfo as ExtendedVehicleComponentInfoCollection,
                vehicleId: data?.vehicleId ?? data?.vehicle?.vehicleId
              })
            )
          );
      })
    ),
  { functional: true, dispatch: true }
);

/**
 * Load Vehicle
 * [start load vehicle]
 *   -> [finish load vehicle]
 */
export const loadVehicle$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startLoadVehicle, acceptChangesAction),
      mergeMap(({ vehicleId }) =>
        store$.select(selectApiContext).pipe(
          first(),
          switchMap(({ market, subProcess, dataVersion }) =>
            appInject<VehiclesService>(VehiclesService).vehicle({ market, subProcess, dataVersion, vehicleId })
          ),
          // TODO: Fix with MBCC-2538
          // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
          mergeMap(vehicle => [
            updateBaumuster({ payload: vehicle.baumuster }),
            updateVehicleId({ payload: vehicle.vehicleId }),
            finishLoadVehicleAction({ vehicle })
          ]),
          catchError((error: HttpErrorResponse) => {
            const errorConfigurator: AppError = {
              statusCode: error.status,
              message: error.message,
              name: error.name,
              type: AppErrorMajor.ResolveBaumuster
            };
            return of(appErrorMajor({ error: errorConfigurator }));
          })
        )
      )
    ),
  { functional: true, dispatch: true }
);

/**
 * When vehicle updated, update context and location state
 * [finish load vehicle] OR [finish update feasible vehicle]
 *   -> [start update vehicle]
 *   -> [start update location state]
 */
export const triggerVehicleUpdate$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishLoadVehicleAction, finishUpdateFeasibleVehicleAction),
      map(({ vehicle }) => startUpdateVehicleAction({ vehicle }))
    ),
  { functional: true, dispatch: true }
);

/**
 * When vehicle updated, update context and location state
 * [finish update vehicle]
 *   -> [start update vehicle]
 *   -> [start update location state]
 */
export const updateVehicle$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startUpdateVehicleAction),
      map(({ vehicle }) => finishUpdateVehicleAction({ vehicle }))
    ),
  { functional: true, dispatch: true }
);

/**
 * When vehicle updated, update context and location state // but what is the "context"?
 * [finish update vehicle]
 *   -> [start update vehicle context]
 *   -> [start update location state]
 */
export const triggerAfterVehicleUpdated$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishUpdateVehicleAction),
      filter(action => !!action.vehicle),
      map(action => action.vehicle),
      // If nothing changed, we don't need to update the context
      distinctUntilChanged(),
      // TODO: Fix with MBCC-2538
      // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
      switchMap((vehicle: MotorizationVehicle) => [
        startUpdateVehicleContextAction({
          vehicleClass: vehicle.vehicleClassId,
          vehicleBody: vehicle.vehicleBodyId,
          vehicleId: vehicle.vehicleId
        }),
        startUpdateLocationStateAction({ vehicleId: vehicle.vehicleId }),
        updateCurrentMotorization({ vehicle })
      ])
    ),
  { functional: true, dispatch: true }
);

/**
 * Update Vehicle Context Data
 * [start update vehicle context]
 *   -> [finish update vehicle context]
 */
export const updateVehicleContext$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startUpdateVehicleContextAction),
      // TODO: Fix with MBCC-2538
      // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
      switchMap(({ vehicleClass, vehicleBody, vehicleId }) => [
        runtimeVariablesUpdate({ payload: { vehicleClass, vehicleBody, vehicleId } }),
        // after a config change and back to motorization, this id would be the configuration candidate
        updateVehicleIdForUpcomingConfiguration({ vehicleId }),
        finishUpdateVehicleContextAction()
      ])
    ),
  { functional: true, dispatch: true }
);

/**
 * Update location state / url with latest vehicle id
 * [start update location state]
 *   -> [finish update location state]
 */
export const updateLocationStateOnVehicleIdChange$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startUpdateLocationStateAction),
      tap(({ vehicleId }) => {
        const replacedPath = appInject<Location>(Location)
          .path()
          .replace(
            // eslint-disable-next-line no-useless-escape
            new RegExp(`(.*/${AppRoutePath.Configuration}/[^/]+/[^/]+/[^/]+/)([^/]+)(/?[\S]*)`),
            `$1${encodeURIComponent(vehicleId)}$3`
          );
        appInject<Location>(Location).replaceState(replacedPath);
      }),
      map(() => finishUpdateLocationStateAction())
    ),
  { functional: true, dispatch: true }
);

/**
 * When starting to load a Vehicle or updated feasible Vehicle, start to load
 * its extended component information. (Only if modelId changed OR no component info has been loaded so far)
 * [start load vehicle] OR [finish update feasible vehicle]
 *   -> [start load componentInfo]
 */
export const triggerLoadComponentInfoWhenLoadVehicle$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startLoadVehicle, finishUpdateFeasibleVehicleAction, acceptChangesAction),
      switchMap(payload =>
        store$.select(selectVehicleInludingComponentInfo).pipe(
          first(),
          map(({ vehicle, componentInfo }) => {
            const vehicleId = payload.vehicleId || (payload as { vehicle: Vehicle }).vehicle.vehicleId; // not nice
            const modelId = getModelIdFromVehicleId(vehicleId);

            // load componentInfo if baumuster+nst=modelId changed
            if (modelId !== vehicle?.modelId || !componentInfo) {
              return startLoadComponentInfoForCurrentVehicleAction({ modelId, vehicleId });
            }

            return finishLoadComponentInfoForCurrentVehicleAction({
              componentInfo,
              vehicleId
            });
          })
        )
      )
    ),
  { functional: true, dispatch: true }
);

/**
 * When componentInfo has been loaded, start to load the Selectables.
 * [finish load component info]
 *   -> [start load selectables]
 */
export const triggerLoadSelectablesWhenFinishLoadComponentInfo$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishLoadComponentInfoForCurrentVehicleAction),
      map(payload => payload.vehicleId),
      map(vehicleId => startLoadSelectablesAction({ vehicleId }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Load Selectables
 * [start load selectables]
 *   -> [finish load selectables]
 */
export const loadSelectables$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)): Observable<any> => {
    // comment why or better naming would be helpful
    const enrichCategory = (category: EnrichedComponentCategory, components: VehicleComponentCollection): void => {
      if (!category.components && category.componentIds) {
        category.components = [];
        for (const id of category.componentIds) {
          if (components[id]) {
            category.components.push(components[id] as VehicleComponent);
          }
        }
      }

      if (category.subcategories) {
        for (const subcategory of category.subcategories) enrichCategory(subcategory, components);
      }
    };

    // comment why or better naming would be helpful
    const enrichSelectables = (
      components: VehicleComponentCollection,
      componentInfo: ExtendedVehicleComponentInfoCollection
    ): void => {
      for (const componentName in components) {
        if (componentInfo?.componentsInfo[componentName]) {
          components[componentName] = {
            ...components[componentName],
            ...componentInfo.componentsInfo[componentName]
          };
        }
      }
    };

    return actions$.pipe(
      ofType(startLoadSelectablesAction),
      mergeMap(({ vehicleId }) =>
        store$.select(selectApiContext).pipe(
          first(),
          concatLatestFrom(() => store$.select(selectConfigurationComponentInfo)),
          mergeMap(([{ market, subProcess, dataVersion }, componentInfo]) =>
            appInject<SelectablesService>(SelectablesService)
              .getSelectables({ market, subProcess, dataVersion, vehicleId })
              .pipe(
                switchMap(
                  ({
                    componentCategories,
                    components,
                    stageV2
                  }: {
                    componentCategories: ComponentCategory[];
                    components: Record<string, SelectablesResponseVehicleComponent>;
                    // TODO: Fix with MBCC-2545
                    // SelectablesService does not provide stageV2. How is this even supposed to work?
                    stageV2: any;
                  }) => {
                    // enrich selectables with extended texts componentInfo
                    enrichSelectables(components, componentInfo);

                    for (const category of componentCategories) {
                      enrichCategory(category, components);
                    }

                    // TODO: Fix with MBCC-2538
                    // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
                    return [
                      // TODO: Fix with MBCC-2545
                      // see comment above. same issue.
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      updateStage({ stage: stageV2, enforceReset: false }),
                      finishLoadSelectablesAction({
                        componentCategories,
                        components
                      })
                    ];
                  }
                ),
                catchError((error: HttpErrorResponse) => {
                  const errorConfigurator: AppErrorWithNotification = {
                    statusCode: error.status,
                    message: error.message,
                    name: error.name,
                    type: AppErrorMinor.LoadSelectables,
                    notification: {
                      message: translate('error.minor.selectables.load'),
                      variant: 'error',
                      dismissable: true,
                      autoDismiss: true
                    }
                  };
                  return of([
                    appErrorMinor({ errorWithNotification: errorConfigurator }),
                    updateStage({ stage: undefined, enforceReset: false }),
                    finishLoadSelectablesAction({
                      componentCategories: [],
                      components: {}
                    })
                  ]);
                })
              )
          )
        )
      )
    );
  },
  { functional: true, dispatch: true }
);

/**
 * When selectables loaded, start to set Navigation Groups
 * [finish load selectables]
 *   -> [start set Navigation Groups]
 */
export const triggerSetNavigationGroups$ = createEffect(
  (actions$ = ngInject(Actions)): Observable<Action<CommonActionType.StartSetNavigationGroups>> =>
    actions$.pipe(
      ofType(finishLoadSelectablesAction),
      map(({ componentCategories }) => startSetNavigationGroups({ componentCategories }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Enqueue Selectable Change
 * [start enqueue selectable change]
 *   -> [finish enqueue selectable change]
 */
export const enqueueSelectableChange$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startEnqueueSelectableChangeAction),
      map(({ selectableChange }) => finishEnqueueSelectableChangeAction({ selectableChange }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Start Request to process next queue item after an item was enqueued or processed
 * [finish enqueue selectable change]
 *   -> [start request selectable change from queue]
 * [finish process selectable change]
 *   -> [start request selectable change from queue]
 */
export const triggerQueueRequestWhenEnqueued$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishEnqueueSelectableChangeAction, finishProcessSelectableChangeAction),
      map(() => startRequestSelectableChangeFromQueueAction())
    ),
  { functional: true, dispatch: true }
);

/**
 * Request to process a Selectable Change from the Queue
 * [start request selectable change from queue]
 *   -> (conditional) [start process selectable change]
 *   -> [finish request selectable change from queue]
 */
export const requestSelectableChangeFromQueue$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startRequestSelectableChangeFromQueueAction),
      mergeMap(() => store$.select(selectSelectableChangeAndQueue).pipe(first())),
      filter(
        ({ selectableChangeInProcess, selectableChangesQueue }) =>
          !selectableChangeInProcess && selectableChangesQueue && selectableChangesQueue.length > 0
      ),
      // TODO: Fix with MBCC-2538
      // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
      switchMap(({ selectableChangesQueue }) => [
        finishRequestSelectableChangeFromQueueAction(),
        startProcessSelectableChangeAction({
          selectableChange: selectableChangesQueue[0]
        })
      ])
    ),
  { functional: true, dispatch: true }
);

/**
 * Start to Process Selectable Change from the Queue
 * [start process selectable change]
 *   -> [start load feasibility result]
 */
export const startProcessSelectableChange$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startProcessSelectableChangeAction),
      map(({ selectableChange }) => startLoadFeasibilityResultAction({ selectableChange }))
    ),
  { functional: true, dispatch: true }
);

const selectApiContextAndVehicleId = createSelector(
  selectApiContext,
  selectConfigurationVehicleId,
  ({ market, subProcess, dataVersion }, vehicleId) => ({ market, subProcess, dataVersion, vehicleId })
);

/**
 * Load a Feasibility Result
 * [start load feasibility result]
 *   -> [finish load feasibility result]
 */
export const loadFeasibilityResult$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startLoadFeasibilityResultAction),
      mergeMap(({ selectableChange: { change, componentId } }) =>
        store$.select(selectApiContextAndVehicleId).pipe(
          first(),
          switchMap(({ market, subProcess, dataVersion, vehicleId }) => {
            const params = {
              market,
              subProcess,
              dataVersion,
              vehicleId,
              componentId
            };

            if (change === VehicleSelectableChange.Add) {
              return appInject<ChangesService>(ChangesService).addComponent(params);
            }
            return appInject<ChangesService>(ChangesService).removeComponent(params);
          }),
          catchError((error: HttpErrorResponse) => {
            const errorConfigurator: AppErrorWithNotification = {
              statusCode: error.status,
              message: error.message,
              name: error.name,
              type: AppErrorMinor.ChangeSelectable,
              notification: {
                message: translate(
                  `error.minor.selectables.${change === VehicleSelectableChange.Add ? 'add' : 'remove'}`
                ),
                variant: 'error',
                dismissable: true,
                autoDismiss: true
              }
            };

            // TODO: Fix with MBCC-2538
            // both lines are false-negattives for @ngrx/no-dispatch-in-effects and @ngrx/avoid-dispatching-multiple-actions-sequentially
            store$.dispatch(appErrorMinor({ errorWithNotification: errorConfigurator }));
            store$.dispatch(finishProcessSelectableChangeAction());

            return EMPTY;
          })
        )
      ),
      // TODO: Fix with MBCC-2545
      // FeasibilityResult (FE) and Feasibility (BE) are not matching. How is this supposed to work?
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      map(feasibilityResult => finishLoadFeasibilityResultAction({ feasibilityResult: feasibilityResult as any }))
    ),
  { functional: true, dispatch: true }
);

/**
 * When Feasibility Result loaded, start to process it
 * [finish load feasibility result]
 *   -> [start process feasibility result]
 */
export const triggerProcessFeasibilityResultWhenFeasibilityResultLoaded$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishLoadFeasibilityResultAction),
      map(({ feasibilityResult }) => startProcessFeasibilityResultAction({ feasibilityResult }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Process Feasibility Result
 * [start process feasibility result]
 *   -> (conditional) [start resolve feasibility conflict]
 *   -> (conditional) [start update feasible vehicle]
 *   -> [finish process feasibility result]
 */
export const processFeasibilityResult$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startProcessFeasibilityResultAction),
      switchMap(({ feasibilityResult: { vehicle, alternatives, conflictItem, conflictDetails } }) => {
        const feasibilityAction = alternatives?.length
          ? startResolveFeasibilityConflictAction({
              alternatives,
              conflictItem,
              vehicle,
              conflictDetails
            })
          : startUpdateFeasibleVehicleAction({ vehicle, conflictItem });
        // TODO: Fix with MBCC-2538
        // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
        return [feasibilityAction, finishProcessFeasibilityResultAction()];
      })
    ),
  { functional: true, dispatch: true }
);

/**
 * Update Vehicle from successful Feasibility Result
 * [start update feasible vehicle]
 *   -> [finish update feasible vehicle]
 */
export const updateFeasibleVehicle$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startUpdateFeasibleVehicleAction),
      map(({ vehicle }) => finishUpdateFeasibleVehicleAction({ vehicle }))
    ),
  { functional: true, dispatch: true }
);

/**
 * Resolve a Feasibility Conflict (by opening a modal and waiting for user feedback)
 * [start resolve feasibility conflict]
 *   -> [finish resolve feasibility conflict]
 */
export const resolveFeasibilityConflict$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startResolveFeasibilityConflictAction),
      mergeMap(({ alternatives, conflictItem, vehicle, conflictDetails }) => {
        appInject<ModalService>(ModalService).open(LazyComponentId.FeasibilityConflictModal, {
          alternatives,
          conflictItem,
          vehicle,
          conflictDetails,
          changeInitiator: AdditionalChangesInitiator.Conflict
        });
        return appInject<ModalService>(ModalService).responseData$.pipe(
          first(),
          switchMap(({ confirmed, data }) => {
            if (!confirmed) {
              return [
                abortFeasibilityConflictAction({
                  feasibilityResult: {
                    conflictItem,
                    alternatives,
                    conflictDetails,
                    changeInitiator: AdditionalChangesInitiator.Conflict
                  }
                }),
                finishProcessSelectableChangeAction()
              ];
            }
            const vehicleId = data.vehicleId as string;
            const feasibilityResult = data.feasibilityResult as FeasibilityResult;
            // TODO: Fix with MBCC-2538
            // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
            return [finishResolveFeasibilityConflictAction({ vehicleId, feasibilityResult })];
          })
        );
      })
    ),
  { functional: true, dispatch: true }
);

/**
 * When a Feasibility Conflict Resolution is finished, start to process the result
 * [finish resolve feasibility conflict]
 *   -> [start process feasibility conflict result]
 */
export const triggerProcessFeasibilityConflictResult$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishResolveFeasibilityConflictAction),
      map(({ vehicleId, feasibilityResult }) =>
        startProcessFeasibilityConflictResultAction({ vehicleId, feasibilityResult })
      )
    ),
  { functional: true, dispatch: true }
);

/**
 * Process Feasibility Conflict Result
 * [start process feasibility conflict result]
 *   -> (conditional) [start load vehicle]
 *   -> [finish process feasibility conflict result]
 */
export const processFeasibilityConflictResult$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startProcessFeasibilityConflictResultAction),
      tap(({ vehicleId }) => {
        if (vehicleId) {
          // TODO: Fix with MBCC-2538
          // this is a false-negative for @ngrx/no-dispatch-in-effects
          store$.dispatch(startLoadVehicle({ vehicleId }));
        }
      }),
      map(() => finishProcessFeasibilityConflictResultAction())
    ),
  { functional: true, dispatch: true }
);

/**
 * When Processing a Feasibility Result is finished, finish the Selectable Change
 * [finish process feasibility conflict result]
 *   -> [finish process selectable change]
 */
export const finishProcessSelectableChangeWhenConflictResolved$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startProcessFeasibilityConflictResultAction),
      map(() => finishProcessSelectableChangeAction())
    ),
  { functional: true, dispatch: true }
);

/**
 * When updating a feasible Vehicle is finished, finish the Selectable Change
 * [finish change feasible vehicle]
 *   -> [finish process selectable change]
 */
export const finishProcessSelectableChangeWhenFeasibleVehicleUpdated$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishUpdateFeasibleVehicleAction),
      map(() => finishProcessSelectableChangeAction())
    ),
  { functional: true, dispatch: true }
);

export const resetOnlineCodeWhenConfigurationChanged$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishProcessSelectableChangeAction),
      map(() => invalidateOnlineCode())
    ),
  { functional: true, dispatch: true }
);

export const resetVehicle$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(resetVehicleAction),
      switchMap(() =>
        appInject(Store)
          .select(selectMotorizationVehiclesByVehicleLengthAndMotorizationType)
          .pipe(
            filterTruthy(),
            first(),
            switchMap(vehicles => {
              const { vehicleId } = vehicles.find(vehicle => vehicle.preselected);
              // TODO: Fix with MBCC-2538
              // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
              return [startLoadVehicle({ vehicleId }), updateVehicleIdForUpcomingConfiguration({ vehicleId })];
            })
          )
      )
    ),
  { functional: true, dispatch: true }
);

export const updateIsInitialMotorization$ = createEffect(
  (store$ = ngInject(Store), actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(updateCurrentMotorization),
      withLatestFrom(store$.select(selectMotorizationVehicles)),
      filter(([action]) => !!action?.vehicle?.vehicleId),
      map(([action, vehicles]) => {
        if (vehicles && vehicles.length > 0) {
          const isInitialMotorization = vehicles.some(vehicle => vehicle.vehicleId === action.vehicle.vehicleId);
          return updateIsInitialMotorizationAction({ isInitialMotorization });
        }
        // default case:
        // no vehicles found, so it's not the initial motorization
        // user directly entered via a configuration page, information just needed on motorization page
        return updateIsInitialMotorizationAction({ isInitialMotorization: false });
      })
    ),
  { functional: true, dispatch: true }
);
